import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../auth';

interface AdminSubscription {
  plan: string;
  billingCycle: string;
  status: string;
  paymentProvider: string;
  trialEndsAt: string | null;
  currentPeriodEnd: string | null;
  canceledAt: string | null;
  updatedAt: string | null;
}

interface AdminUser {
  userId: string;
  email: string | null;
  connectedProvider: string | null;
  gmailConnected: boolean;
  lastIngestedAt: string | null;
  backfillCompleted: boolean;
  backfillPageToken: string | null;
  backfillStartedAt: string | null;
  backfillFailureCount: number;
  backfillLastError: string | null;
  connectedAt: string | null;
  subscription: AdminSubscription | null;
}

interface AdminUserListRecord {
  userId: string;
  email: string | null;
  status: string | null;
  plan: string | null;
  billingCycle: string | null;
  connectedProvider: string | null;
  backfillCompleted: boolean;
  lastIngestedAt: string | null;
  needsReauth: boolean;
}

interface AdminCategoryCount { category: string; count: number; }
interface AdminTopSender { senderEmail: string; senderName: string | null; category: string | null; emailCount: number; }
interface AdminUserSummary { totalEmails: number; byCategory: AdminCategoryCount[]; topSenders: AdminTopSender[]; }

interface GmailLabelCount { id: string; name: string; messagesTotal: number; messagesUnread: number; }
interface GmailLabelHealth { labels: GmailLabelCount[]; fetchedAt: string; }

interface ActivityFeedItem { kind: string; at: string; summary: string; details: string; }

const PLANS = ['starter', 'pro'];
const CYCLES = ['monthly', 'annual'];
const STATUSES = ['none', 'trialing', 'active', 'past_due', 'canceled'];
const PROVIDERS = ['pending', 'stripe', 'gocardless', 'complimentary'];
const PAGE_SIZE = 20;

const STATUS_COLORS: Record<string, string> = {
  active: 'var(--success)',
  trialing: '#4a86e8',
  past_due: '#ffad47',
  canceled: 'var(--text-secondary)',
  none: 'var(--text-secondary)',
};

function toDateInputValue(iso: string | null): string {
  if (!iso) return '';
  return iso.slice(0, 10);
}

function toIso(dateInput: string): string | null {
  if (!dateInput) return null;
  return new Date(dateInput).toISOString();
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

function relativeTime(iso: string | null): string {
  if (!iso) return 'Never';
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 2) return 'Just now';
  if (mins < 120) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function StatusBadge({ status }: { status: string | null }) {
  const color = status ? (STATUS_COLORS[status] ?? 'var(--text-secondary)') : 'var(--text-secondary)';
  return (
    <span style={{ fontSize: 12, color, fontWeight: status === 'active' ? 600 : undefined }}>
      {status ?? '—'}
    </span>
  );
}

function ProviderBadge({ provider }: { provider: 'gmail' | 'microsoft' }) {
  const isGmail = provider === 'gmail';
  return (
    <span style={{
      display: 'inline-block', fontSize: 11, fontWeight: 500, padding: '1px 6px',
      borderRadius: 4, marginLeft: 8, verticalAlign: 'middle',
      background: isGmail ? '#e8f0fe' : '#e3f2fd',
      color: isGmail ? '#1a56c4' : '#0a66c2',
    }}>
      {isGmail ? 'Gmail only' : 'Microsoft only'}
    </span>
  );
}

export default function AdminUsersPage() {
  const { user: authUser } = useAuth();

  // ── Grid state ──────────────────────────────────────────────────────────────
  const [gridSearch, setGridSearch]       = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter]   = useState('');
  const [page, setPage]                   = useState(1);
  const [gridLoading, setGridLoading]     = useState(false);
  const [gridUsers, setGridUsers]         = useState<AdminUserListRecord[]>([]);
  const [totalUsers, setTotalUsers]       = useState(0);
  const [gridRefreshKey, setGridRefreshKey] = useState(0);
  const [isNarrow, setIsNarrow] = useState(window.innerWidth < 600);

  // ── Selected user (manage panel) ────────────────────────────────────────────
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [loadingUser, setLoadingUser]     = useState(false);
  const [loadUserError, setLoadUserError] = useState<string | null>(null);
  const [user, setUser]                   = useState<AdminUser | null>(null);
  const managePanelRef                    = useRef<HTMLDivElement>(null);

  const [plan, setPlan]         = useState('');
  const [cycle, setCycle]       = useState('');
  const [status, setStatus]     = useState('');
  const [provider, setProvider] = useState('');
  const [trialEndsAt, setTrialEndsAt] = useState('');
  const [periodEnd, setPeriodEnd]     = useState('');
  const [saving, setSaving]     = useState(false);
  const [saveMsg, setSaveMsg]   = useState<{ ok: boolean; text: string } | null>(null);

  const [actionLimitInput, setActionLimitInput] = useState('');
  const [actionLimitMsg, setActionLimitMsg]     = useState<{ ok: boolean; text: string } | null>(null);
  const [savingLimit, setSavingLimit]           = useState(false);
  const [resettingActions, setResettingActions] = useState(false);
  const [resetActionsMsg, setResetActionsMsg]   = useState<{ ok: boolean; text: string } | null>(null);

  const [confirmReset, setConfirmReset] = useState(false);
  const [resetting, setResetting]       = useState(false);
  const [resetMsg, setResetMsg]         = useState<{ ok: boolean; text: string } | null>(null);
  const [ingestCap, setIngestCap]       = useState('');

  const [syncingSenders, setSyncingSenders]       = useState(false);
  const [syncSendersMsg, setSyncSendersMsg]       = useState<{ ok: boolean; text: string } | null>(null);
  const [ensuringLabels, setEnsuringLabels]       = useState(false);
  const [ensureLabelsMsg, setEnsureLabelsMsg]     = useState<{ ok: boolean; text: string } | null>(null);
  const [applyingLabels, setApplyingLabels]       = useState(false);
  const [applyLabelsMsg, setApplyLabelsMsg]       = useState<{ ok: boolean; text: string } | null>(null);
  const [correctingLabels, setCorrectingLabels]   = useState(false);
  const [correctLabelsMsg, setCorrectLabelsMsg]   = useState<{ ok: boolean; text: string } | null>(null);
  const [resumingBackfill, setResumingBackfill]   = useState(false);
  const [resumeBackfillMsg, setResumeBackfillMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [triggeringCleanup, setTriggeringCleanup] = useState(false);
  const [triggerCleanupMsg, setTriggerCleanupMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [triggeringSync, setTriggeringSync]       = useState(false);
  const [triggerSyncMsg, setTriggerSyncMsg]       = useState<{ ok: boolean; text: string } | null>(null);

  const [confirmWipe, setConfirmWipe] = useState(false);
  const [wiping, setWiping]           = useState(false);
  const [wipeMsg, setWipeMsg]         = useState<{ ok: boolean; text: string } | null>(null);

  const [userSummary, setUserSummary]       = useState<AdminUserSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const [activity, setActivity]           = useState<ActivityFeedItem[] | null>(null);
  const [activityLoading, setActivityLoading] = useState(false);

  const [gmailLabelHealth, setGmailLabelHealth] = useState<GmailLabelHealth | null>(null);
  const [gmailLabelLoading, setGmailLabelLoading] = useState(false);
  const [gmailLabelError, setGmailLabelError]   = useState<string | null>(null);

  // ── Resize listener ──────────────────────────────────────────────────────────
  useEffect(() => {
    const handler = () => setIsNarrow(window.innerWidth < 600);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // ── Debounce grid search ─────────────────────────────────────────────────────
  useEffect(() => {
    const t = setTimeout(() => {
      if (debouncedSearch !== gridSearch) {
        setPage(1);
        setDebouncedSearch(gridSearch);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [gridSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load grid ────────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setGridLoading(true);
      try {
        const params = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) });
        if (debouncedSearch) params.set('search', debouncedSearch);
        if (statusFilter)    params.set('status', statusFilter);
        const res = await fetch(`/api/mgmt/users?${params}`);
        if (!cancelled && res.ok) {
          const data = await res.json();
          setGridUsers(data.users);
          setTotalUsers(data.total);
        }
      } catch {}
      finally { if (!cancelled) setGridLoading(false); }
    };
    load();
    return () => { cancelled = true; };
  }, [debouncedSearch, statusFilter, page, gridRefreshKey]);

  // ── Scroll to manage panel when user is selected ─────────────────────────────
  useEffect(() => {
    if (user) {
      setTimeout(() => managePanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    }
  }, [user?.userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Helpers ──────────────────────────────────────────────────────────────────
  function populateForm(u: AdminUser) {
    const s = u.subscription;
    setPlan(s?.plan ?? 'starter');
    setCycle(s?.billingCycle ?? 'monthly');
    setStatus(s?.status ?? 'none');
    setProvider(s?.paymentProvider ?? 'pending');
    setTrialEndsAt(toDateInputValue(s?.trialEndsAt ?? null));
    setPeriodEnd(toDateInputValue(s?.currentPeriodEnd ?? null));
    setConfirmReset(false);
  }

  function clearMessages() {
    setSaveMsg(null); setResetMsg(null); setActionLimitMsg(null); setResetActionsMsg(null);
    setSyncSendersMsg(null); setEnsureLabelsMsg(null); setApplyLabelsMsg(null);
    setCorrectLabelsMsg(null); setResumeBackfillMsg(null); setTriggerCleanupMsg(null);
    setTriggerSyncMsg(null);
  }

  async function handleManageUser(userId: string) {
    setSelectedUserId(userId);
    setLoadingUser(true);
    setLoadUserError(null);
    setUser(null);
    setUserSummary(null);
    setActivity(null);
    setGmailLabelHealth(null);
    setGmailLabelError(null);
    clearMessages();
    try {
      const res = await fetch('/api/mgmt/users/lookup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: userId }),
      });
      if (!res.ok) { setLoadUserError('Failed to load user.'); return; }
      const data: AdminUser = await res.json();
      setUser(data);
      populateForm(data);
      setSummaryLoading(true);
      fetch(`/api/mgmt/users/${data.userId}/summary`)
        .then(r => r.ok ? r.json() : Promise.reject())
        .then((s: AdminUserSummary) => setUserSummary(s))
        .catch(() => {})
        .finally(() => setSummaryLoading(false));

      setActivityLoading(true);
      fetch(`/api/mgmt/users/${data.userId}/activity`)
        .then(r => r.ok ? r.json() : Promise.reject())
        .then((items: ActivityFeedItem[]) => setActivity(items))
        .catch(() => setActivity([]))
        .finally(() => setActivityLoading(false));
    } catch {
      setLoadUserError('Failed to load user.');
    } finally {
      setLoadingUser(false);
    }
  }

  async function handleSaveSubscription() {
    if (!user) return;
    if (status === 'active' && !periodEnd) {
      setSaveMsg({ ok: false, text: 'Period end is required when status is Active.' });
      return;
    }
    setSaving(true); setSaveMsg(null);
    try {
      const body: Record<string, unknown> = { plan, billingCycle: cycle, status, paymentProvider: provider };
      if (trialEndsAt) body.trialEndsAt = toIso(trialEndsAt);
      if (periodEnd)   body.currentPeriodEnd = toIso(periodEnd);
      const res = await fetch(`/api/mgmt/users/${user.userId}/subscription`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      if (!res.ok) { setSaveMsg({ ok: false, text: 'Save failed.' }); return; }
      setSaveMsg({ ok: true, text: 'Saved.' });
      setGridRefreshKey(k => k + 1);
      const refreshed = await fetch('/api/mgmt/users/lookup', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: user.userId }),
      });
      if (refreshed.ok) setUser(await refreshed.json());
    } catch {
      setSaveMsg({ ok: false, text: 'Save failed.' });
    } finally { setSaving(false); }
  }

  async function handleResetActions() {
    if (!user) return;
    setResettingActions(true); setResetActionsMsg(null);
    try {
      const res = await fetch(`/api/mgmt/users/${user.userId}/reset-actions`, { method: 'POST' });
      setResetActionsMsg(res.ok ? { ok: true, text: 'Action count reset to 0.' } : { ok: false, text: 'Reset failed.' });
    } catch { setResetActionsMsg({ ok: false, text: 'Reset failed.' }); }
    finally { setResettingActions(false); }
  }

  async function handleSetActionLimit(limit: number | null) {
    if (!user) return;
    setSavingLimit(true); setActionLimitMsg(null);
    try {
      const res = await fetch(`/api/mgmt/users/${user.userId}/action-limit`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ limit }),
      });
      if (res.ok) {
        setActionLimitMsg({ ok: true, text: limit === null ? 'Override cleared.' : `Override set to ${limit}.` });
        if (limit === null) setActionLimitInput('');
      } else {
        setActionLimitMsg({ ok: false, text: 'Save failed.' });
      }
    } catch { setActionLimitMsg({ ok: false, text: 'Save failed.' }); }
    finally { setSavingLimit(false); }
  }

  async function handleResetIngest() {
    if (!user) return;
    setResetting(true); setResetMsg(null); setConfirmReset(false);
    const capValue = ingestCap ? parseInt(ingestCap, 10) : null;
    try {
      const res = await fetch(`/api/mgmt/users/${user.userId}/reset-ingest`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(capValue ? { maxEmails: capValue } : {}),
      });
      if (!res.ok) { setResetMsg({ ok: false, text: 'Reset failed.' }); return; }
      setResetMsg({ ok: true, text: 'Ingest reset queued — deletes are running in the background. Monitor the backfill health card for when re-ingestion begins.' });
      const refreshed = await fetch('/api/mgmt/users/lookup', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ query: user.userId }),
      });
      if (refreshed.ok) { const data = await refreshed.json(); setUser(data); populateForm(data); }
    } catch { setResetMsg({ ok: false, text: 'Reset failed.' }); }
    finally { setResetting(false); }
  }

  async function handleSyncSenders() {
    if (!user) return;
    setSyncingSenders(true); setSyncSendersMsg(null);
    try {
      const res = await fetch(`/api/mgmt/users/${user.userId}/sync-senders`, { method: 'POST' });
      setSyncSendersMsg(res.ok ? { ok: true, text: 'Senders synced.' } : { ok: false, text: 'Sync failed.' });
    } catch { setSyncSendersMsg({ ok: false, text: 'Sync failed.' }); }
    finally { setSyncingSenders(false); }
  }

  async function handleEnsureLabels() {
    if (!user) return;
    setEnsuringLabels(true); setEnsureLabelsMsg(null);
    try {
      const res = await fetch(`/api/mgmt/users/${user.userId}/ensure-labels`, { method: 'POST' });
      if (res.ok) {
        setEnsureLabelsMsg({ ok: true, text: 'Labels ensured.' });
      } else {
        const data = await res.json().catch(() => ({}));
        setEnsureLabelsMsg({ ok: false, text: (data as any).error ?? `Failed (${res.status}).` });
      }
    } catch { setEnsureLabelsMsg({ ok: false, text: 'Network error.' }); }
    finally { setEnsuringLabels(false); }
  }

  async function handleApplyLabels() {
    if (!user) return;
    setApplyingLabels(true); setApplyLabelsMsg(null);
    try {
      const res = await fetch(`/api/mgmt/users/${user.userId}/apply-labels`, { method: 'POST' });
      if (res.status === 202) {
        setApplyLabelsMsg({ ok: true, text: 'Label backfill queued — refresh the Gmail label health card in a few minutes to monitor progress.' });
      } else {
        const data = await res.json().catch(() => ({}));
        setApplyLabelsMsg({ ok: false, text: (data as any).error ?? `Failed (${res.status}).` });
      }
    } catch { setApplyLabelsMsg({ ok: false, text: 'Network error.' }); }
    finally { setApplyingLabels(false); }
  }

  async function handleCorrectLabels() {
    if (!user) return;
    setCorrectingLabels(true); setCorrectLabelsMsg(null);
    try {
      const res = await fetch(`/api/mgmt/users/${user.userId}/correct-labels`, { method: 'POST' });
      if (res.status === 202) {
        setCorrectLabelsMsg({ ok: true, text: 'Label numbering correction queued — check Gmail label health in a few minutes.' });
      } else {
        const data = await res.json().catch(() => ({}));
        setCorrectLabelsMsg({ ok: false, text: (data as any).error ?? `Failed (${res.status}).` });
      }
    } catch { setCorrectLabelsMsg({ ok: false, text: 'Network error.' }); }
    finally { setCorrectingLabels(false); }
  }

  async function handleResumeBackfill() {
    if (!user) return;
    setResumingBackfill(true); setResumeBackfillMsg(null);
    try {
      const res = await fetch(`/api/mgmt/users/${user.userId}/resume-backfill`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        const cursor = data.resumedFromToken ? `cursor ${data.resumedFromToken.slice(0, 16)}…` : 'start';
        const poisonNote = data.poisonMessagesCleared > 0 ? `, ${data.poisonMessagesCleared} poison message(s) cleared` : '';
        setResumeBackfillMsg({ ok: true, text: `Backfill re-queued from ${cursor}${poisonNote}` });
        const refreshed = await fetch('/api/mgmt/users/lookup', {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ query: user.userId }),
        });
        if (refreshed.ok) { const data2 = await refreshed.json(); setUser(data2); populateForm(data2); }
      } else {
        const data = await res.json().catch(() => ({}));
        setResumeBackfillMsg({ ok: false, text: (data as any).error ?? 'Resume failed.' });
      }
    } catch { setResumeBackfillMsg({ ok: false, text: 'Resume failed.' }); }
    finally { setResumingBackfill(false); }
  }

  async function handleTriggerCleanup() {
    if (!user) return;
    setTriggeringCleanup(true); setTriggerCleanupMsg(null);
    try {
      const res = await fetch(`/api/mgmt/users/${user.userId}/trigger-cleanup`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        setTriggerCleanupMsg({ ok: true, text: `Cleanup enqueued (sweep #${data.sweepRequestId}).` });
      } else {
        const data = await res.json().catch(() => ({}));
        setTriggerCleanupMsg({ ok: false, text: (data as any).error ?? 'Trigger failed.' });
      }
    } catch { setTriggerCleanupMsg({ ok: false, text: 'Trigger failed.' }); }
    finally { setTriggeringCleanup(false); }
  }

  async function handleTriggerSync() {
    if (!user) return;
    setTriggeringSync(true); setTriggerSyncMsg(null);
    try {
      const res = await fetch(`/api/mgmt/users/${user.userId}/trigger-sync`, { method: 'POST' });
      if (res.status === 202) {
        setTriggerSyncMsg({ ok: true, text: 'Sync enqueued — the next delta fetch will run within a minute.' });
      } else {
        const data = await res.json().catch(() => ({}));
        setTriggerSyncMsg({ ok: false, text: (data as any).error ?? `Failed (${res.status}).` });
      }
    } catch { setTriggerSyncMsg({ ok: false, text: 'Network error.' }); }
    finally { setTriggeringSync(false); }
  }

  async function handleWipeMyData() {
    if (!authUser?.userId) return;
    setWiping(true); setWipeMsg(null); setConfirmWipe(false);
    try {
      const enqueueRes = await fetch(`/api/mgmt/users/${authUser.userId}/delete-all-data`, { method: 'POST' });
      if (!enqueueRes.ok) {
        const body = await enqueueRes.json().catch(() => ({}));
        setWipeMsg({ ok: false, text: `Wipe failed: ${(body as any).error ?? enqueueRes.status}` });
        setWiping(false);
        return;
      }
      setWipeMsg({ ok: false, text: 'Deletion in progress…' });
      const deadline = Date.now() + 60_000;
      const poll = async (): Promise<void> => {
        if (Date.now() > deadline) {
          setWipeMsg({ ok: false, text: 'Taking longer than expected — check back shortly.' });
          setWiping(false);
          return;
        }
        const statusRes = await fetch(`/api/mgmt/users/${authUser.userId}/delete-status`);
        if (statusRes.ok) {
          const data = await statusRes.json();
          if (data.status === 'complete') { setWiping(false); window.location.href = '/.auth/logout'; return; }
          if (data.status === 'failed')   { setWipeMsg({ ok: false, text: 'Deletion failed — please try again or contact support.' }); setWiping(false); return; }
        }
        setTimeout(() => { poll(); }, 2000);
      };
      setTimeout(() => { poll(); }, 2000);
    } catch { setWipeMsg({ ok: false, text: 'Wipe failed.' }); setWiping(false); }
  }

  async function handleLoadGmailLabelHealth() {
    if (!user) return;
    setGmailLabelLoading(true); setGmailLabelError(null);
    try {
      const res = await fetch(`/api/mgmt/users/${user.userId}/gmail-label-health`);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setGmailLabelError((data as any).error ?? `Failed (${res.status}).`);
        return;
      }
      setGmailLabelHealth(await res.json());
    } catch { setGmailLabelError('Network error.'); }
    finally { setGmailLabelLoading(false); }
  }

  const totalPages = Math.max(1, Math.ceil(totalUsers / PAGE_SIZE));

  const isMicrosoft = user?.connectedProvider === 'microsoft';
  const gmailOnlyDisabledProps = isMicrosoft
    ? { disabled: true, title: 'This action only applies to Gmail accounts.' }
    : {};

  return (
    <>
      <div className="page-header">
        <h1>Users</h1>
      </div>

      {/* ── Users grid ─────────────────────────────────────────────────────── */}
      <div className="dashboard-card" style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
          <div className="card-label">All users</div>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            {gridLoading ? 'Loading…' : `${totalUsers.toLocaleString()} user${totalUsers !== 1 ? 's' : ''}`}
          </span>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
          <input
            className="search-input"
            type="text"
            placeholder="Search by email or user ID"
            value={gridSearch}
            onChange={e => setGridSearch(e.target.value)}
            style={{ flex: 1, minWidth: 220 }}
          />
          <select
            className="search-input"
            value={statusFilter}
            onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
            style={{ width: 150 }}
          >
            <option value="">All statuses</option>
            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        {/* Table */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                <th style={{ textAlign: 'left', padding: '4px 12px 8px 0', fontWeight: 500, color: 'var(--text-secondary)' }}>Email</th>
                <th style={{ textAlign: 'left', padding: '4px 12px 8px 0', fontWeight: 500, color: 'var(--text-secondary)' }}>Status</th>
                {!isNarrow && <th style={{ textAlign: 'left', padding: '4px 12px 8px 0', fontWeight: 500, color: 'var(--text-secondary)' }}>Plan</th>}
                <th style={{ textAlign: 'left', padding: '4px 12px 8px 0', fontWeight: 500, color: 'var(--text-secondary)' }}>Account</th>
                {!isNarrow && <th style={{ textAlign: 'left', padding: '4px 12px 8px 0', fontWeight: 500, color: 'var(--text-secondary)' }}>Last synced</th>}
                <th />
              </tr>
            </thead>
            <tbody>
              {gridLoading && gridUsers.length === 0 ? (
                <tr>
                  <td colSpan={isNarrow ? 4 : 6} style={{ padding: '16px 0', color: 'var(--text-secondary)', textAlign: 'center' }}>Loading…</td>
                </tr>
              ) : gridUsers.length === 0 ? (
                <tr>
                  <td colSpan={isNarrow ? 4 : 6} style={{ padding: '16px 0', color: 'var(--text-secondary)', textAlign: 'center' }}>No users found.</td>
                </tr>
              ) : gridUsers.map(u => (
                <tr
                  key={u.userId}
                  style={{
                    borderBottom: '1px solid var(--border)',
                    background: u.userId === selectedUserId ? 'var(--bg-secondary, rgba(0,0,0,0.03))' : undefined,
                  }}
                >
                  <td style={{ padding: '8px 12px 8px 0' }}>
                    <div>{u.email ?? <em style={{ color: 'var(--text-secondary)' }}>no email</em>}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontFamily: 'monospace' }}>
                      {u.userId ? (u.userId.length > 24 ? `${u.userId.slice(0, 24)}…` : u.userId) : '—'}
                    </div>
                  </td>
                  <td style={{ padding: '8px 12px 8px 0' }}>
                    <StatusBadge status={u.status} />
                  </td>
                  {!isNarrow && (
                    <td style={{ padding: '8px 12px 8px 0', textTransform: 'capitalize', color: 'var(--text-secondary)' }}>
                      {u.plan ?? '—'}
                    </td>
                  )}
                  <td style={{ padding: '8px 12px 8px 0' }}>
                    {u.connectedProvider
                      ? u.needsReauth && u.status !== 'canceled' && u.status !== 'none'
                        ? <span style={{ color: 'var(--danger)', fontSize: 12 }}>⚠ Needs reauth</span>
                        : <span style={{ color: 'var(--success)', fontSize: 12 }}>
                            {u.connectedProvider === 'gmail' ? 'Gmail' : u.connectedProvider === 'microsoft' ? 'Outlook' : u.connectedProvider}
                          </span>
                      : <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>—</span>
                    }
                  </td>
                  {!isNarrow && (
                    <td style={{ padding: '8px 12px 8px 0', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                      {relativeTime(u.lastIngestedAt)}
                    </td>
                  )}
                  <td style={{ padding: '8px 0', textAlign: 'right' }}>
                    <button
                      className="btn-secondary"
                      style={{ fontSize: 12, padding: '3px 10px' }}
                      onClick={() => handleManageUser(u.userId)}
                      disabled={loadingUser}
                    >
                      {loadingUser && u.userId === selectedUserId ? 'Loading…' : 'Manage'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12, fontSize: 13 }}>
          <button
            className="btn-secondary"
            style={{ fontSize: 12, padding: '3px 10px' }}
            onClick={() => setPage(p => p - 1)}
            disabled={page <= 1 || gridLoading}
          >
            ← Previous
          </button>
          <span style={{ color: 'var(--text-secondary)' }}>
            Page {page} of {totalPages}
          </span>
          <button
            className="btn-secondary"
            style={{ fontSize: 12, padding: '3px 10px' }}
            onClick={() => setPage(p => p + 1)}
            disabled={page >= totalPages || gridLoading}
          >
            Next →
          </button>
        </div>
      </div>

      {loadUserError && (
        <p className="state-message state-error" style={{ marginBottom: 16 }}>{loadUserError}</p>
      )}

      {/* ── Manage panel ───────────────────────────────────────────────────── */}
      {user && (
        <div ref={managePanelRef}>

          {/* Inbox summary */}
          <div className="dashboard-card" style={{ marginBottom: 24 }}>
            <div className="card-label" style={{ marginBottom: 12 }}>
              Inbox summary — {user.email ?? user.userId}
            </div>
            {summaryLoading ? (
              <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Loading…</p>
            ) : userSummary ? (
              <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
                <div style={{ minWidth: 180 }}>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 8 }}>Emails by category</div>
                  <table style={{ fontSize: 13, borderCollapse: 'collapse', width: '100%' }}>
                    <tbody>
                      {userSummary.byCategory.map(c => (
                        <tr key={c.category}>
                          <td style={{ padding: '2px 12px 2px 0', textTransform: 'capitalize' }}>{c.category}</td>
                          <td style={{ padding: '2px 0', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{c.count.toLocaleString()}</td>
                        </tr>
                      ))}
                      <tr style={{ borderTop: '1px solid var(--border)', fontWeight: 600 }}>
                        <td style={{ padding: '4px 12px 2px 0' }}>Total</td>
                        <td style={{ padding: '4px 0 2px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{userSummary.totalEmails.toLocaleString()}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div style={{ flex: 1, minWidth: 280 }}>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 8 }}>Top senders by volume</div>
                  <table style={{ fontSize: 13, borderCollapse: 'collapse', width: '100%' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--border)' }}>
                        <th style={{ textAlign: 'left', padding: '2px 12px 4px 0', fontWeight: 500, color: 'var(--text-secondary)' }}>Sender</th>
                        <th style={{ textAlign: 'left', padding: '2px 12px 4px 0', fontWeight: 500, color: 'var(--text-secondary)' }}>Category</th>
                        <th style={{ textAlign: 'right', padding: '2px 0 4px', fontWeight: 500, color: 'var(--text-secondary)' }}>Emails</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userSummary.topSenders.map(s => (
                        <tr key={s.senderEmail} style={{ borderBottom: '1px solid var(--border)' }}>
                          <td style={{ padding: '4px 12px 4px 0' }}>
                            <div>{s.senderName ?? s.senderEmail}</div>
                            {s.senderName && <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{s.senderEmail}</div>}
                          </td>
                          <td style={{ padding: '4px 12px 4px 0', textTransform: 'capitalize', color: 'var(--text-secondary)' }}>{s.category ?? '—'}</td>
                          <td style={{ padding: '4px 0', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{s.emailCount.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>No inbox data yet.</p>
            )}
          </div>

          {/* Activity feed */}
          <div className="dashboard-card" style={{ marginBottom: 24 }}>
            <div className="card-label" style={{ marginBottom: 12 }}>Activity</div>
            {activityLoading ? (
              <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Loading…</p>
            ) : activity && activity.length > 0 ? (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ fontSize: 12, borderCollapse: 'collapse', width: '100%' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                      <th style={{ textAlign: 'left', padding: '2px 12px 6px 0', fontWeight: 500, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>When</th>
                      <th style={{ textAlign: 'left', padding: '2px 12px 6px 0', fontWeight: 500, color: 'var(--text-secondary)' }}>Kind</th>
                      <th style={{ textAlign: 'left', padding: '2px 0 6px', fontWeight: 500, color: 'var(--text-secondary)' }}>Summary</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activity.map((item, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--border)' }} title={item.details}>
                        <td style={{ padding: '4px 12px 4px 0', whiteSpace: 'nowrap', color: 'var(--text-secondary)' }}>
                          {new Date(item.at).toLocaleString()}
                        </td>
                        <td style={{ padding: '4px 12px 4px 0', whiteSpace: 'nowrap' }}>
                          <span style={{
                            fontSize: 11, padding: '1px 6px', borderRadius: 4,
                            background: item.kind === 'webhook' ? 'rgba(74,134,232,0.15)' : 'rgba(0,128,0,0.1)',
                            color: item.kind === 'webhook' ? '#4a86e8' : 'var(--success)',
                          }}>
                            {item.kind === 'webhook' ? 'webhook' : 'sub change'}
                          </span>
                        </td>
                        <td style={{ padding: '4px 0', wordBreak: 'break-word' }}>{item.summary}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>No activity recorded yet.</p>
            )}
          </div>

          {/* Gmail label health */}
          {user.gmailConnected && (
            <div className="dashboard-card" style={{ marginBottom: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <div className="card-label">Gmail label health</div>
                <button
                  className="btn-secondary"
                  style={{ fontSize: 12, padding: '2px 10px' }}
                  onClick={handleLoadGmailLabelHealth}
                  disabled={gmailLabelLoading}
                >
                  {gmailLabelLoading ? 'Loading…' : gmailLabelHealth ? 'Refresh' : 'Load'}
                </button>
              </div>
              {gmailLabelError && <p style={{ fontSize: 13, color: 'var(--danger)' }}>{gmailLabelError}</p>}
              {!gmailLabelHealth && !gmailLabelError && (
                <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Live counts from Gmail. Click Load to fetch.</p>
              )}
              {gmailLabelHealth && (
                <>
                  <table style={{ fontSize: 13, borderCollapse: 'collapse', width: '100%' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--border)' }}>
                        <th style={{ textAlign: 'left', padding: '2px 12px 6px 0', fontWeight: 500, color: 'var(--text-secondary)' }}>Label</th>
                        <th style={{ textAlign: 'right', padding: '2px 12px 6px 0', fontWeight: 500, color: 'var(--text-secondary)' }}>Messages</th>
                        <th style={{ textAlign: 'right', padding: '2px 0 6px', fontWeight: 500, color: 'var(--text-secondary)' }}>Unread</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gmailLabelHealth.labels.map(l => (
                        <tr key={l.id} style={{ borderBottom: '1px solid var(--border)' }}>
                          <td style={{ padding: '4px 12px 4px 0', fontFamily: l.name === 'INBOX' ? undefined : 'monospace', fontSize: l.name === 'INBOX' ? 13 : 12 }}>
                            {l.name === 'INBOX' ? 'Inbox' : l.name.replace('The Email Helper/', '')}
                          </td>
                          <td style={{ padding: '4px 12px 4px 0', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{l.messagesTotal.toLocaleString()}</td>
                          <td style={{ padding: '4px 0', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: l.messagesUnread > 0 ? 'var(--danger)' : undefined }}>
                            {l.messagesUnread > 0 ? l.messagesUnread.toLocaleString() : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 6 }}>
                    Fetched {new Date(gmailLabelHealth.fetchedAt).toLocaleTimeString()}
                  </p>
                </>
              )}
            </div>
          )}

          {/* User detail + actions card */}
          <div className="dashboard-card" style={{ marginBottom: 24 }}>
            {/* Identity */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 4 }}>User ID</div>
              <code style={{ fontSize: 13 }}>{user.userId}</code>
              <div style={{ marginTop: 8, fontSize: 13, color: 'var(--text-secondary)' }}>Email</div>
              <div>{user.email ?? <em style={{ color: 'var(--text-secondary)' }}>not stored yet</em>}</div>
              <div style={{ marginTop: 8, fontSize: 13, color: 'var(--text-secondary)' }}>Mail account</div>
              <div>{user.connectedProvider
                ? `${user.connectedProvider === 'gmail' ? 'Gmail' : user.connectedProvider === 'microsoft' ? 'Outlook' : user.connectedProvider} — last ingested: ${formatDate(user.lastIngestedAt)}`
                : 'Not connected'}</div>
              {user.gmailConnected && (() => {
                const isStuck = !user.backfillCompleted
                  && !!user.lastIngestedAt
                  && (Date.now() - new Date(user.lastIngestedAt).getTime()) > 2 * 60 * 60 * 1000;
                const neverStarted = !user.backfillCompleted && !user.backfillStartedAt && !!user.connectedAt;
                const connectedMins = user.connectedAt
                  ? Math.floor((Date.now() - new Date(user.connectedAt).getTime()) / 60000)
                  : 0;
                const statusLabel = user.backfillCompleted ? 'Complete'
                  : neverStarted
                    ? `Never started (${connectedMins >= 60 ? Math.floor(connectedMins / 60) + 'h' : connectedMins + 'min'} since connect)`
                  : isStuck ? 'Stuck / possibly poisoned'
                  : 'In progress';
                const statusColor = user.backfillCompleted ? 'var(--success)'
                  : (isStuck || neverStarted) ? 'var(--danger)' : 'var(--text-secondary)';
                return (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 4 }}>Backfill status</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 13, color: statusColor, fontWeight: (isStuck || neverStarted) ? 600 : undefined }}>{statusLabel}</span>
                      {user.backfillStartedAt && (
                        <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                          Started {new Date(user.backfillStartedAt).toLocaleString()}
                        </span>
                      )}
                      {user.backfillPageToken && (
                        <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'monospace' }}>
                          cursor: {user.backfillPageToken.slice(0, 20)}…
                        </span>
                      )}
                      {!user.backfillCompleted && (
                        <button
                          className="btn-secondary"
                          onClick={handleResumeBackfill}
                          disabled={resumingBackfill}
                          style={{ fontSize: 12, padding: '3px 10px' }}
                          title="Re-enqueues the ingestion job from the last stored cursor without wiping data. Use when a backfill chunk was dead-lettered."
                        >
                          {resumingBackfill ? 'Queuing…' : 'Resume backfill'}
                        </button>
                      )}
                    </div>
                    {user.backfillFailureCount > 0 && (
                      <div style={{ marginTop: 6, fontSize: 12, color: 'var(--danger)' }}>
                        {user.backfillFailureCount} failure(s){user.backfillLastError ? `: ${user.backfillLastError}` : ''}
                      </div>
                    )}
                    <p style={{ marginTop: 6, fontSize: 12, color: 'var(--text-secondary)' }}>
                      Re-enqueues the ingestion job from the last stored cursor without wiping data.
                      Use when a backfill chunk was dead-lettered.
                    </p>
                    {resumeBackfillMsg && (
                      <p style={{ marginTop: 6, fontSize: 13, color: resumeBackfillMsg.ok ? 'var(--success)' : 'var(--danger)' }}>
                        {resumeBackfillMsg.text}
                      </p>
                    )}
                  </div>
                );
              })()}
            </div>

            <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '16px 0' }} />

            {/* Subscription editor */}
            <div style={{ marginBottom: 16, fontWeight: 600 }}>Subscription</div>
            {!user.subscription && (
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>
                No subscription record — fill in the fields below and save to create one.
              </p>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, marginBottom: 12 }}>
              <label style={{ fontSize: 13 }}>
                <div style={{ color: 'var(--text-secondary)', marginBottom: 4 }}>Plan</div>
                <select className="search-input" value={plan} onChange={e => setPlan(e.target.value)}>
                  {PLANS.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </label>
              <label style={{ fontSize: 13 }}>
                <div style={{ color: 'var(--text-secondary)', marginBottom: 4 }}>Billing cycle</div>
                <select className="search-input" value={cycle} onChange={e => setCycle(e.target.value)}>
                  {CYCLES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              <label style={{ fontSize: 13 }}>
                <div style={{ color: 'var(--text-secondary)', marginBottom: 4 }}>Status</div>
                <select className="search-input" value={status} onChange={e => setStatus(e.target.value)}>
                  {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
              <label style={{ fontSize: 13 }}>
                <div style={{ color: 'var(--text-secondary)', marginBottom: 4 }}>Payment provider</div>
                <select className="search-input" value={provider} onChange={e => setProvider(e.target.value)}>
                  {PROVIDERS.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </label>
              {status === 'trialing' && (
                <label style={{ fontSize: 13 }}>
                  <div style={{ color: 'var(--text-secondary)', marginBottom: 4 }}>Trial ends at</div>
                  <input className="search-input" type="date" value={trialEndsAt} onChange={e => setTrialEndsAt(e.target.value)} />
                </label>
              )}
              <label style={{ fontSize: 13 }}>
                <div style={{ color: 'var(--text-secondary)', marginBottom: 4 }}>
                  Period end{status === 'active' && <span style={{ color: 'var(--danger)', marginLeft: 3 }}>*</span>}
                </div>
                <input
                  className="search-input" type="date" value={periodEnd}
                  onChange={e => setPeriodEnd(e.target.value)}
                  style={status === 'active' && !periodEnd ? { borderColor: 'var(--danger)' } : undefined}
                />
              </label>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <button className="btn-primary" onClick={handleSaveSubscription} disabled={saving}>
                {saving ? 'Saving…' : 'Save subscription'}
              </button>
              {saveMsg && <span style={{ fontSize: 13, color: saveMsg.ok ? 'var(--success)' : 'var(--danger)' }}>{saveMsg.text}</span>}
            </div>

            <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '20px 0' }} />

            {/* Action limits */}
            <div style={{ marginBottom: 8, fontWeight: 600 }}>Action limits</div>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>
              Reset the free action counter or set a per-user override cap. Use when a trial user
              runs out of free actions before their trial ends and needs more headroom.
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
              <button className="btn-secondary" onClick={handleResetActions} disabled={resettingActions}>
                {resettingActions ? 'Resetting…' : 'Reset action count'}
              </button>
              {resetActionsMsg && <span style={{ fontSize: 13, color: resetActionsMsg.ok ? 'var(--success)' : 'var(--danger)' }}>{resetActionsMsg.text}</span>}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <input
                className="search-input" type="number" min={0} placeholder="Override limit"
                value={actionLimitInput} onChange={e => setActionLimitInput(e.target.value)} style={{ width: 140 }}
              />
              <button className="btn-secondary" onClick={() => handleSetActionLimit(parseInt(actionLimitInput, 10))} disabled={savingLimit || !actionLimitInput}>Set override</button>
              <button className="btn-ghost" onClick={() => handleSetActionLimit(null)} disabled={savingLimit}>Clear override</button>
              {actionLimitMsg && <span style={{ fontSize: 13, color: actionLimitMsg.ok ? 'var(--success)' : 'var(--danger)' }}>{actionLimitMsg.text}</span>}
            </div>

            <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '20px 0' }} />

            {/* Reset ingest */}
            <div style={{ marginBottom: 8, fontWeight: 600 }}>Reset inbox ingest</div>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>
              Deletes all emails and senders for this user and restarts ingestion from scratch.
              Destructive — use only when the stored data is known to be corrupt or stale.
              The delete runs asynchronously in the background queue; backfill is re-enqueued on completion.
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
              <label style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Ingest cap (optional)</label>
              <input className="search-input" type="number" min={1} placeholder="e.g. 500" value={ingestCap} onChange={e => setIngestCap(e.target.value)} style={{ width: 120 }} />
              <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Leave blank for full ingest</span>
            </div>
            {!confirmReset ? (
              <button className="btn-secondary btn-secondary--danger" onClick={() => setConfirmReset(true)} disabled={resetting}>Reset inbox ingest</button>
            ) : (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13, color: 'var(--danger)' }}>This will delete all emails and senders and trigger a full re-ingest. Continue?</span>
                <button className="btn-secondary btn-secondary--danger" onClick={handleResetIngest} disabled={resetting}>{resetting ? 'Resetting…' : 'Confirm reset'}</button>
                <button className="btn-ghost" onClick={() => setConfirmReset(false)}>Cancel</button>
              </div>
            )}
            {resetMsg && <p style={{ marginTop: 8, fontSize: 13, color: resetMsg.ok ? 'var(--success)' : 'var(--danger)' }}>{resetMsg.text}</p>}

            <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '20px 0' }} />

            {/* Sync senders */}
            <div style={{ marginBottom: 8, fontWeight: 600 }}>Sync senders</div>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>
              Rebuilds the senders table from the emails already in the database — useful after a backfill
              if subscriptions are not showing. Does not re-ingest any emails.
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <button className="btn-secondary" onClick={handleSyncSenders} disabled={syncingSenders}>
                {syncingSenders ? 'Syncing…' : 'Sync senders'}
              </button>
              {syncSendersMsg && <span style={{ fontSize: 13, color: syncSendersMsg.ok ? 'var(--success)' : 'var(--danger)' }}>{syncSendersMsg.text}</span>}
            </div>

            <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '20px 0' }} />

            {/* Ensure Gmail labels */}
            <div style={{ marginBottom: 8, fontWeight: 600 }}>
              Ensure Gmail labels<ProviderBadge provider="gmail" />
            </div>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>
              Recreates any missing Email Helper Gmail labels. Safe to run at any time — existing labels are left unchanged.
              Use when a user reports missing labels or after a Gmail token re-auth.
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <button className="btn-secondary" onClick={handleEnsureLabels} disabled={ensuringLabels || isMicrosoft} {...gmailOnlyDisabledProps}>
                {ensuringLabels ? 'Ensuring…' : 'Ensure labels'}
              </button>
              {ensureLabelsMsg && <span style={{ fontSize: 13, color: ensureLabelsMsg.ok ? 'var(--success)' : 'var(--danger)' }}>{ensureLabelsMsg.text}</span>}
            </div>

            <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '20px 0' }} />

            {/* Apply labels + sweep */}
            <div style={{ marginBottom: 8, fontWeight: 600 }}>
              Apply labels &amp; sweep inbox<ProviderBadge provider="gmail" />
            </div>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>
              Backfills Gmail category labels for all classified emails, then runs inbox retention to archive
              emails that exceed their threshold. Use after reclassification or when a user reports labels are missing.
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <button className="btn-secondary" onClick={handleApplyLabels} disabled={applyingLabels || isMicrosoft} {...gmailOnlyDisabledProps}>
                {applyingLabels ? 'Queueing…' : 'Apply labels & sweep'}
              </button>
              {applyLabelsMsg && <span style={{ fontSize: 13, color: applyLabelsMsg.ok ? 'var(--success)' : 'var(--danger)' }}>{applyLabelsMsg.text}</span>}
            </div>

            <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '20px 0' }} />

            {/* Correct label numbering */}
            <div style={{ marginBottom: 8, fontWeight: 600 }}>
              Correct label numbering<ProviderBadge provider="gmail" />
            </div>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>
              Fixes label numbering if OTP was incorrectly placed as label 1. Only needed if a user was
              affected before the OTP label position fix (May 2026).
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <button className="btn-secondary" onClick={handleCorrectLabels} disabled={correctingLabels || isMicrosoft} {...gmailOnlyDisabledProps}>
                {correctingLabels ? 'Queueing…' : 'Correct label numbering'}
              </button>
              {correctLabelsMsg && <span style={{ fontSize: 13, color: correctLabelsMsg.ok ? 'var(--success)' : 'var(--danger)' }}>{correctLabelsMsg.text}</span>}
            </div>

            <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '20px 0' }} />

            {/* Trigger inbox cleanup */}
            <div style={{ marginBottom: 8, fontWeight: 600 }}>
              Trigger inbox cleanup<ProviderBadge provider="gmail" />
            </div>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>
              Queues an inbox sweep for the user — scans uncategorised senders via LLM and removes stale
              To Respond labels. Use when a user's inbox hasn't been cleaned up or after a backfill completes.
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <button className="btn-secondary" onClick={handleTriggerCleanup} disabled={triggeringCleanup || isMicrosoft} {...gmailOnlyDisabledProps}>
                {triggeringCleanup ? 'Queueing…' : 'Trigger cleanup'}
              </button>
              {triggerCleanupMsg && <span style={{ fontSize: 13, color: triggerCleanupMsg.ok ? 'var(--success)' : 'var(--danger)' }}>{triggerCleanupMsg.text}</span>}
            </div>

            {/* Force delta sync — Microsoft only */}
            {isMicrosoft && (
              <>
                <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '20px 0' }} />
                <div style={{ marginBottom: 8, fontWeight: 600 }}>
                  Force delta sync<ProviderBadge provider="microsoft" />
                </div>
                <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>
                  Enqueues an immediate incremental sync for this Microsoft account. Use when a user reports
                  emails are stale and you don't want to wait for the next 15-minute timer tick.
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <button className="btn-secondary" onClick={handleTriggerSync} disabled={triggeringSync}>
                    {triggeringSync ? 'Queueing…' : 'Force delta sync'}
                  </button>
                  {triggerSyncMsg && <span style={{ fontSize: 13, color: triggerSyncMsg.ok ? 'var(--success)' : 'var(--danger)' }}>{triggerSyncMsg.text}</span>}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── My account ─────────────────────────────────────────────────────── */}
      <div className="dashboard-card" style={{ marginBottom: 24 }}>
        <div className="card-label" style={{ marginBottom: 4 }}>My account</div>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>
          Permanently deletes all your personal data — emails, senders, actions, tokens, and subscription record.
          Use this to re-register as a fresh account for testing. This method will also power GDPR erasure requests.
        </p>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>
          User ID: <code>{authUser?.userId ?? '—'}</code>
        </div>
        {!confirmWipe ? (
          <button className="btn-secondary btn-secondary--danger" onClick={() => setConfirmWipe(true)} disabled={wiping || !authUser?.userId}>
            Wipe all my data
          </button>
        ) : (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, color: 'var(--danger)' }}>This permanently deletes everything. There is no undo. Continue?</span>
            <button className="btn-secondary btn-secondary--danger" onClick={handleWipeMyData} disabled={wiping}>
              {wiping ? 'Deleting…' : 'Confirm — delete everything'}
            </button>
            <button className="btn-ghost" onClick={() => setConfirmWipe(false)}>Cancel</button>
          </div>
        )}
        {wipeMsg && (
          <p style={{ marginTop: 8, fontSize: 13, color: wiping ? 'var(--text-secondary)' : wipeMsg.ok ? 'var(--success)' : 'var(--danger)' }}>
            {wipeMsg.text}
          </p>
        )}
      </div>
    </>
  );
}
